import { CompanyContext } from 'contexts/CompanyContextProvider';
import React, { useContext, useState } from 'react';
import { Subtype } from 'services/EpdClient';
import EPDService from 'services/EpdService';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { createTag } from 'services/api/epdApi';
import {
  addEpdFile,
  useAddCoverPageFile,
  useChangeEPDDefinition,
  useChangeEpdTags,
  useRemoveCoverPageFile,
  useUpdateEpdAdditionalInformation,
  useUpsertLCASpecification,
} from 'services/api/mutations';
import {
  useDictionaryValues,
  useEPDDefinitionInformation,
  useEpdAdditionalInformation,
  useEpdCollectionsLookup,
  useEpdCoverPageInformation,
  useEpdOrganizationInformation,
  useGetLCASpecification,
  useTagSource,
} from 'services/api/queries';
import {
  ElementWithHelpBox,
  ExpandablePanelHeaderName,
  FieldPanelFullWidth,
  FieldPanelHeader,
  HelpBoldText,
  HelpBoxContainer,
} from 'styles/v2/Styles.styled';
import { CustomLogotypeId } from 'types/CustomLogotypeId';
import { EPDBasedOnUnitEnum, LCASpecificationModel, Option } from 'types/types';
import DebugOnly from 'util/dev-tools/DebugOnly';
import getSubtypeLabel from 'util/getSubtypeLabel';
import { getOptionsFromDictionary } from 'util/utils';

import { confirmAlert } from '../confirm-dialog/confirmAlert';
import CompilerSelectControlledComponent from '../epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardCreatableSelectControlledComponent from '../epd-wizard-inputs/WizardCreatableSelectControlled';
import WizardHTMLEditor from '../epd-wizard-inputs/WizardHTMLEditor';
import WizardTextAreaComponent from '../epd-wizard-inputs/WizardTextArea';
import WizardTextInputComponent from '../epd-wizard-inputs/WizardTextInput';
import { ExpandableHelpBox, HelpBox, HelpBoxBoldText } from '../help-boxes';
import TooltipHelpIcon from '../icons/TooltipHelpIcon';
import { ImagesUpload } from '../images-upload';
import SmartLabel from '../labels/SmartLabel';
import { EPDProductClassificationId } from './constants';
import VersionHistoryDialog from './dialogs/VersionHistoryDialog';
import ToggleButton from './panels/toggleable/ToggleButton';
import ConversionFactorSection from './sections/specification/ConversionFactorSection';
import ReferenceFlowSection from './sections/specification/ReferenceFlowSection';
import SkeletonDefinitionTab from './skeletons/SkeletonDefinitionTab';
import usePcrV2, { usePcrV2Loading } from './hooks/usePcrV2';

type LabelsAccordingToPCRModel = {
  versionDescription: string;
  versionDescriptionTooltip: string;
  sitesSelectionDescription: string;
  documentCoverageStatement: string;
  documentCoverageStatementTooltip: string;
  epdClassificationDictionaryCode: string;
};
const DefinitionTab: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string;
  validationState: any;
}> = ({ epdId, epdVersionId, validationState }) => {
  const { errors } = validationState || {};
  const [isConversionFactorLock, setIsConversionFactorLock] = useState<boolean>(false);

  const epdDefinitionInformation = useEPDDefinitionInformation(epdVersionId).data;
  const dictionariesValues = useDictionaryValues().data;
  const additionalInformation = useEpdAdditionalInformation(epdVersionId).data;
  const coverPageInformation = useEpdCoverPageInformation(epdVersionId).data;
  const organizationInformation = useEpdOrganizationInformation(epdVersionId).data;
  const lcaSpecification = useGetLCASpecification(epdVersionId!).data;
  const { companyId } = useContext(CompanyContext);
  const epdCollectionsLookup = useEpdCollectionsLookup(companyId ?? '').data;

  const addCoverPageFileMutation = useAddCoverPageFile(epdVersionId);
  const deleteCoverPageFileMutation = useRemoveCoverPageFile(epdVersionId);
  const tagSource = useTagSource().data;
  const changeEpdTagsMutation = useChangeEpdTags(epdVersionId);
  const updateEPDDefinitionMutation = useChangeEPDDefinition(epdVersionId);
  const updateAdditionalInformationMutation = useUpdateEpdAdditionalInformation(epdVersionId);
  const updateMutationLCASpecification = useUpsertLCASpecification(epdVersionId);
  const isPcrV2Loading = usePcrV2Loading();
  const isPcrV2 = usePcrV2();

  const isLoading =
    epdDefinitionInformation === undefined ||
    dictionariesValues === undefined ||
    additionalInformation === undefined ||
    coverPageInformation === undefined ||
    lcaSpecification === undefined ||
    epdCollectionsLookup === undefined ||
    isPcrV2Loading ||
    validationState === undefined;
  if (isLoading) {
    return <SkeletonDefinitionTab />;
  }

  const labelsAccordingToPCR: LabelsAccordingToPCRModel = isPcrV2
    ? {
        versionDescription: 'Version history',
        versionDescriptionTooltip:
          'A section shall be included describing the current and previous versions of the EPD, including the version dates. The first version shall be described as the "original version of the EPD". For each subsequent version, a description of the differences versus the previously published version shall be included.',
        sitesSelectionDescription:
          'How the selection of the sites/products has been done and the average/Worst-case has been determined',
        documentCoverageStatement: 'Statement about average/worst-case values',
        documentCoverageStatementTooltip:
          'A statement that the EPD covers the average/worst-case values for an entire or partial product category (specifying the percentage of representativeness) and, hence, the declared product is an average/worst-case that is not available for purchase on the market.',
        epdClassificationDictionaryCode: EPD_DICTIONARY_CODES.EPD_CLASSIFICATION_V2,
      }
    : {
        versionDescription: 'Version description',
        versionDescriptionTooltip: 'Provide the description of a new version changes.',
        sitesSelectionDescription:
          'How the selection of the sites/products has been done and the average has been determined',
        documentCoverageStatement:
          'A statement that the EPD covers average values for an entire or partial product category specifying the percentage of representativeness and, hence, the declared product is an average that is not available for purchase on the market and that the results are not representative for any specific manufacturer or its product.',
        documentCoverageStatementTooltip:
          'A statement that the EPD covers average values for an entire or partial product category specifying the percentage of representativeness and, hence, the declared product is an average that is not available for purchase on the market and that the results are not representative for any specific manufacturer or its product.',
        epdClassificationDictionaryCode: EPD_DICTIONARY_CODES.EPD_CLASSIFICATION,
      };

  const epdBasedOnUnitOptions = getOptionsFromDictionary(dictionariesValues, EPD_DICTIONARY_CODES.EPD_BASED_ON_UNIT);

  const referenceFlowUnits =
    dictionariesValues.filter((x) => x.dictionaryCode === EPD_DICTIONARY_CODES.REFERENCE_FLOW_UNIT) || [];

  const tagsValue = epdDefinitionInformation.tags || [];

  const onChangeEpdTags = async (propertyName: string, val: any) => {
    changeEpdTagsMutation.mutate({
      epdId: epdId,
      versionId: epdVersionId,
      propertyName: propertyName,
      newValue: val,
    });
  };

  const handleCreateTag = async (inputValue: string) => {
    const newOption = await createTag({ id: null, name: inputValue });
    onChangeEpdTags('tags', [...tagsValue, { label: newOption.name, value: newOption.id } as Option]);
  };

  const onChangeEpdAdditionalInformation = async (propertyName: string, val: any) => {
    updateAdditionalInformationMutation.mutate({
      epdId: epdId,
      versionId: epdVersionId,
      propertyName: propertyName,
      newValue: val,
    });
  };

  const onAddCoverPageFile = async (propertyName: string, file: File) => {
    await addEpdFile({ epdId: epdId, propertyName, file }, addCoverPageFileMutation);
  };

  const onRemoveCoverPageFile = async (fileId: string) => {
    deleteCoverPageFileMutation.mutate(fileId);
  };

  const onChangeLCASpecification = async (propertyName: string, val: any) => {
    const newData = { ...lcaSpecification, [propertyName]: val } as LCASpecificationModel;

    if (propertyName === 'lcaSoftwareId') {
      newData.lcaSoftwareVersionId = undefined;
    }

    updateMutationLCASpecification.mutate(newData);
  };

  const epdClassificationOptions = getOptionsFromDictionary(
    dictionariesValues,
    labelsAccordingToPCR.epdClassificationDictionaryCode
  );

  const getSubTypeOptionDisabled = (key: string) => {
    switch (key) {
      case Subtype.Specific:
        return [
          EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_AVG_RESULTS_ID,
          EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_REPRESENTATIVE_PRODUCT_ID,
          EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_SEVERAL_REPRESENTATIVE_PRODUCTS_ID,
          EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_WORST_CASE_RESULTS_ID,
          EPDProductClassificationId.SECTOR_OF_MULTIPLE_PRODUCTS_ID,
          EPDProductClassificationId.SECTOR_OF_AVERAGE_PRODUCTS_ID,
          EPDProductClassificationId.SECTOR_OF_WORST_CASE_PRODUCTS_ID,
        ].includes(epdDefinitionInformation.epdClassification!);
      case Subtype.Average:
        return [
          EPDProductClassificationId.SECTOR_OF_MULTIPLE_PRODUCTS_ID,
          EPDProductClassificationId.SECTOR_OF_AVERAGE_PRODUCTS_ID,
          EPDProductClassificationId.SECTOR_OF_WORST_CASE_PRODUCTS_ID,
          EPDProductClassificationId.SINGLE_PRODUCT_ID,
        ].includes(epdDefinitionInformation.epdClassification!);
      case Subtype.Representative:
        return [
          EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_AVG_RESULTS_ID,
          EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_REPRESENTATIVE_PRODUCT_ID,
          EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_SEVERAL_REPRESENTATIVE_PRODUCTS_ID,
          EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_WORST_CASE_RESULTS_ID,
          EPDProductClassificationId.SINGLE_PRODUCT_ID,
        ].includes(epdDefinitionInformation.epdClassification!);

      default:
        return false;
    }
  };

  const subtypeOptions: Option[] = (Object.keys(Subtype) as Array<keyof typeof Subtype>).map((key) => ({
    label: getSubtypeLabel(Subtype[key]),
    value: Subtype[key].toString(),
    disabled: getSubTypeOptionDisabled(key),
  }));

  const getSubtypeValue = () => {
    if (epdDefinitionInformation.subtype) {
      return subtypeOptions.find((x) => x.value == epdDefinitionInformation.subtype?.toString() || '');
    }
    return {
      label: '',
      value: '',
    };
  };

  const productTypeOptions = getOptionsFromDictionary(dictionariesValues, EPD_DICTIONARY_CODES.PRODUCT_TYPE);

  const conformityStandardsOptions = getOptionsFromDictionary(dictionariesValues, EPD_DICTIONARY_CODES.CONFORMITY_STANDARDS);

  const selectedEPDBasedOnUnit = epdBasedOnUnitOptions.find(
    (x) =>
      lcaSpecification?.epdBasedOnUnit &&
      x.value === Object.keys(EPDBasedOnUnitEnum).indexOf(lcaSpecification?.epdBasedOnUnit).toString()
  );

  const selectedProductTypeOriginal = !isNaN(Number(epdDefinitionInformation.productType))
    ? productTypeOptions.find((x) => x.value == epdDefinitionInformation.productType)
    : productTypeOptions.find((x) => x.label == epdDefinitionInformation.productType);

  const selectedProductType = selectedProductTypeOriginal
    ? ({
        value: selectedProductTypeOriginal?.value.toString(),
        label: selectedProductTypeOriginal?.label,
      } as Option)
    : null;

  const { declaredStandards } = epdDefinitionInformation;
  function getSelectedStandards(declaredStandards: string | undefined, conformityStandardsOptions: any[]) {
    if (!declaredStandards) return [];

    const standardsArray = declaredStandards?.split(',');

    return standardsArray?.map((x: any) => conformityStandardsOptions.find((y) => y.value == x));
  }

  const onChangeEpdClassification = async (propertyName: string, newValue: any, oldValue: any) => {
    if (newValue == EPDProductClassificationId.SINGLE_PRODUCT_ID && oldValue) {
      confirmAlert({
        caption: 'Are you sure you want to switch to a single product?',
        description: (
          <div>
            Switching to a single product will <b>erase all products and product-related data</b> entered in subsequent
            steps.
          </div>
        ),
        acceptLabel: 'Submit',
        rejectLabel: 'Cancel',
        onConfirm: () => {
          onChangeEPDDefinitionProperty(propertyName, newValue);
        },
        onReject: () => {},
      });

      return;
    }

    onChangeEPDDefinitionProperty(propertyName, newValue);
  };

  const onChangeEPDDefinitionProperty = async (propertyName: string, val: any) => {
    updateEPDDefinitionMutation.mutate({
      epdId: epdId,
      versionId: epdVersionId,
      propertyName: propertyName,
      newValue: val,
    });
  };

  const isOptionDisabled = (option: any) => {
    return option.disabled;
  };

  const headerHelpBoxTemplate = (options: any, header: string, tooltip?: string, tooltipPosition?: any | undefined) => {
    return (
      <div className={options.className}>
        <ExpandablePanelHeaderName>
          <HelpBoldText>{header}</HelpBoldText>
          {tooltip && <TooltipHelpIcon content={tooltip} position={tooltipPosition} />}
        </ExpandablePanelHeaderName>
        <ToggleButton options={options} withMargins />
      </div>
    );
  };

  const licenseeName = organizationInformation?.licenseeName;
  const collocationCenterName = organizationInformation?.collocationCenterName;
  const companyBelongsToDifferentGeographicalGroups = licenseeName && collocationCenterName;

  const logotypeOptions = [
    { label: 'Licensee logotype', value: CustomLogotypeId.LICENSEE_LOGOTYPE_ID.toString() },
    { label: 'CLC logotype', value: CustomLogotypeId.COLLOCATION_CENTER_LOGOTYPE_ID.toString() },
  ];

  const selectedLogotype = logotypeOptions.find((x) => x.value === epdDefinitionInformation?.customLogotype?.toString());

  return (
    <>
      <FieldPanelFullWidth>
        <WizardTextInputComponent
          label="UUID of data set"
          name="uuid"
          tooltip="Automatically generated universally unique identifier of this data."
          disabled
          value={epdId}
        />
        <DebugOnly>
          <WizardTextInputComponent
            label="version UUID"
            name="epdVersionId"
            tooltip="Automatically generated universally unique identifier of this data."
            disabled
            value={epdVersionId}
          />
        </DebugOnly>
        <WizardTextInputComponent
          label="EPD Id"
          name="registrationNumber"
          tooltip="Automatically generated unique identifier for this EPD."
          disabled
          value={epdDefinitionInformation.fullIdentificationNumber}
        />
        <div>
          <WizardTextInputComponent
            label="EPD Version"
            name="versionNumber"
            tooltip="Automatically generated version number to track the EPD changes."
            disabled
            value={`${String(epdDefinitionInformation.versionNumber ?? 1).padStart(3, '0')}`}
          />
          <VersionHistoryDialog epdId={epdId} versions={epdDefinitionInformation.epdVersions ?? []} />
        </div>
        {(isPcrV2 || epdDefinitionInformation.versionNumber !== 1) && (
          <WizardTextAreaComponent
            label={labelsAccordingToPCR.versionDescription}
            tooltip={labelsAccordingToPCR.versionDescriptionTooltip}
            tooltipPosition="right"
            placeholder="Type here"
            name="versionDescription"
            value={epdDefinitionInformation.versionDescription}
            onChanged={onChangeEPDDefinitionProperty}
            error={validationState.errors.versionDescription}
            required
          />
        )}
        <WizardTextInputComponent
          label="EPD name"
          tooltip="Add the product name which will be stated as EPD name for both - in the EPD portal and in in the front cover page of the EPD document."
          tooltipPosition={'right'}
          required
          value={epdDefinitionInformation.declarationName}
          onChanged={onChangeEPDDefinitionProperty}
          name="title"
          maxLength={150}
          error={validationState.errors.title}
        />
        <CompilerSelectControlledComponent
          label="Single or multiple products"
          tooltip="Choose an option based on whether the EPD covers a single product or multiple products, as well as how the results of the product group are presented. If one of the options on multiple products is selected list the products that are included in the EPD."
          name="epdClassification"
          options={epdClassificationOptions}
          value={epdClassificationOptions.find((x) => Number(x.value) === epdDefinitionInformation.epdClassification)}
          onChanged={onChangeEpdClassification}
          placeholder="Select..."
          isClearable={false}
          required
          error={validationState.errors.epdClassification}
        />
        {/* #10964 'key' attribute is needed to reset defaultValue of dependent text fields */}
        <React.Fragment key={epdDefinitionInformation.epdClassification}>
          {[
            EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_REPRESENTATIVE_PRODUCT_ID,
            EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_SEVERAL_REPRESENTATIVE_PRODUCTS_ID,
          ].includes(epdDefinitionInformation.epdClassification!) && (
            <WizardTextAreaComponent
              label="Justification for why this is representative"
              placeholder="Type here"
              name="representativeJustification"
              value={epdDefinitionInformation.representativeJustification}
              onChanged={onChangeEPDDefinitionProperty}
              required
              error={validationState.errors.representativeJustification}
            />
          )}
          {epdDefinitionInformation.epdClassification ===
            EPDProductClassificationId.MULTIPLE_PRODUCTS_BASED_ON_AVG_RESULTS_ID && (
            <WizardTextAreaComponent
              label="Description of how the averages have been determined"
              placeholder="Type here"
              name="determinationOfAverageDescription"
              value={epdDefinitionInformation.determinationOfAverageDescription}
              onChanged={onChangeEPDDefinitionProperty}
              required
              error={validationState.errors.determinationOfAverageDescription}
            />
          )}
          {[
            EPDProductClassificationId.SECTOR_OF_MULTIPLE_PRODUCTS_ID,
            EPDProductClassificationId.SECTOR_OF_AVERAGE_PRODUCTS_ID,
            EPDProductClassificationId.SECTOR_OF_WORST_CASE_PRODUCTS_ID,
          ].includes(epdDefinitionInformation.epdClassification!) && (
            <>
              <WizardTextAreaComponent
                label="A list of the contributing manufacturers that the Sector EPD covers"
                placeholder="Type here"
                name="listOfContributingManufacturers"
                value={epdDefinitionInformation.listOfContributingManufacturers}
                onChanged={onChangeEPDDefinitionProperty}
                required
                error={validationState.errors.listOfContributingManufacturers}
              />
              <WizardTextAreaComponent
                label={labelsAccordingToPCR.sitesSelectionDescription}
                placeholder="Type here"
                name="sitesSelectionDescription"
                value={epdDefinitionInformation.sitesSelectionDescription}
                onChanged={onChangeEPDDefinitionProperty}
                required
                error={validationState.errors.sitesSelectionDescription}
              />
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
                <WizardTextAreaComponent
                  labelStyle={EllipsisLabelStyles}
                  label={labelsAccordingToPCR.documentCoverageStatement}
                  tooltip={labelsAccordingToPCR.documentCoverageStatementTooltip}
                  placeholder="Type here"
                  name="documentCoverageStatement"
                  value={epdDefinitionInformation.documentCoverageStatement}
                  onChanged={onChangeEPDDefinitionProperty}
                  required
                  error={validationState.errors.documentCoverageStatement}
                />
              </div>
            </>
          )}
        </React.Fragment>
        <CompilerSelectControlledComponent
          label="Product to declare"
          tooltip="EPD can declare goods or services, choose between the two options."
          name="productType"
          options={productTypeOptions}
          required
          value={selectedProductType}
          onChanged={onChangeEPDDefinitionProperty}
          placeholder="Select..."
          isClearable={false}
          error={validationState.errors.productToDeclare}
        />
        <CompilerSelectControlledComponent
          label="Statement of conformity with"
          tooltip="Select the standards with which the EPD is compliant."
          name="declaredStandards"
          options={conformityStandardsOptions}
          required
          value={getSelectedStandards(declaredStandards, conformityStandardsOptions)}
          onChanged={onChangeEPDDefinitionProperty}
          placeholder="Select..."
          isMulti
          error={validationState.errors.declaredStandards}
        />
        {/* US #9798: Temporarily removed everything Okobaudat-related
        
        <CompilerSelectControlledComponent
          label="Require EPD Publication on ÖKOBAUDAT"
          tooltip="With the ÖKOBAUDAT platform, the Federal Ministry for Housing, Urban Development and Building (BMWSB) Germany provides all stakeholders with a standardized database for the life cycle assessment of buildings."
          name="requirePublicationOnOkobaudat"
          options={YesNoDataSource}
          value={YesNoDataSource.find((item) => item.value === epdDefinitionInformation.requirePublicationOnOkobaudat)}
          onChanged={onChangeEPDDefinitionProperty}
          isClearable={false}
          placeholder="Select"
          error={validationState.errors.requirePublicationOnOkobaudat}
          required
        /> */}
        <WizardCreatableSelectControlledComponent
          label="Keywords"
          tooltip="With the keywords you can help to find your valid EPD documents in a quick way in our EPD library on environdec.com."
          components={{ DropdownIndicator: null }}
          value={tagsValue || []}
          name="tags"
          isMulti
          isClearable={false}
          placeholder="Type here to add keyword..."
          options={tagSource?.map((tag) => {
            return { value: tag.id, label: tag.name };
          })}
          onCreateOption={handleCreateTag}
          onChanged={onChangeEpdTags}
        />
        <CompilerSelectControlledComponent
          label="Add to collection"
          tooltip="With the EPD Collection pages you can organize and attractively present your valid EPD documents in a hierarchical, but flexible organisational structure to your stakeholders via our EPD Library."
          name="collections"
          options={epdCollectionsLookup}
          value={epdDefinitionInformation.collections}
          onChanged={onChangeEPDDefinitionProperty}
          placeholder="Select..."
          isMulti
          disabled={!(epdCollectionsLookup && epdCollectionsLookup.length > 0)}
        />
        <div>
          <SmartLabel
            label={'Product image for cover page'}
            required
            hasValue={!!coverPageInformation?.coverPageProductImage}
            tooltip="Product image which will present your product in the front cover page of the EPD document."
            error={validationState.errors.coverPageProductImage}
          />
          <ImagesUpload
            name={'CoverPageProductImage'}
            single
            images={coverPageInformation?.coverPageProductImage ? [coverPageInformation?.coverPageProductImage] : []}
            onUpload={onAddCoverPageFile}
            onReorder={(imgIds: string[]) => EPDService.arrangeEpdFilesByModifyDate(imgIds)}
            onRemove={onRemoveCoverPageFile}
          />
          <HelpBoxContainer>
            <HelpBox>
              <span>
                Upload an image with a recommended resolution of 150–300 DPI. Images will be proportionally adjusted to fit
                the page size. To see image placement in the document, use the "PDF preview" option in the upper right
                corner.
              </span>
            </HelpBox>
          </HelpBoxContainer>
        </div>
        <WizardTextAreaComponent
          label="Use advice for the EPD"
          placeholder="Type here"
          tooltip="Specification on how the EPD is to be used. If there is no information to provide, please enter 'No information available' or similar."
          name="useAdvice"
          value={epdDefinitionInformation.useAdvice ?? ''}
          onChanged={onChangeEPDDefinitionProperty}
          required
          error={validationState.errors.useAdvice}
        />
        <ElementWithHelpBox>
          <CompilerSelectControlledComponent
            isClearable={false}
            required
            label="Subtype for ILCD+EPD"
            tooltip="Indicates the type of data set regarding its representativeness. This field is mandatory for ILCD+EPD format. Please note that only one option can be selected."
            tooltipPosition="top | right"
            options={subtypeOptions}
            name="subtype"
            value={getSubtypeValue()}
            onChanged={onChangeEPDDefinitionProperty}
            error={validationState.errors.subtype}
            isOptionDisabled={isOptionDisabled}
          />
          <ExpandableHelpBox headerTemplate={(options) => headerHelpBoxTemplate(options, 'What is subtype?')} toggleable>
            <span>
              Select a subtype that indicates the representativeness of EPD. One of the following predefined data types shall
              be chosen:
              <li>Specific (single product).</li>
              <li>Average (multiple products).</li>
              <li>Representative (Sector EPD).</li>
              <li>Template (product not yet on the market).</li>
            </span>
          </ExpandableHelpBox>
        </ElementWithHelpBox>
      </FieldPanelFullWidth>

      <FieldPanelFullWidth>
        <CompilerSelectControlledComponent
          label="EPD based on declared or functional unit"
          tooltip="The declared or functional unit is the reference unit to which the product's environmental performance is related. Functional unit is defined as a quantified performance of a product system and a declared unit is defined as a quantity of a product."
          name="epdBasedOnUnit"
          options={epdBasedOnUnitOptions}
          value={selectedEPDBasedOnUnit}
          onChanged={onChangeLCASpecification}
          required={true}
          error={errors?.lcaSpecification?.epdBasedOnUnit}
          placeholder="Select"
          isClearable={false}
        />
      </FieldPanelFullWidth>
      {lcaSpecification?.epdBasedOnUnit && lcaSpecification?.epdBasedOnUnit === EPDBasedOnUnitEnum.FunctionalUnit && (
        <FieldPanelFullWidth>
          <FieldPanelHeader>
            Functional unit
            <TooltipHelpIcon content="A functional unit is defined as a quantified performance of a product system." />
          </FieldPanelHeader>
          <WizardTextAreaComponent
            label="Functional unit description"
            placeholder="Type here"
            name="functionalUnitDescription"
            value={lcaSpecification?.functionalUnitDescription}
            error={errors?.lcaSpecification?.functionalUnitDescription}
            required
            onChanged={onChangeLCASpecification}
          />
        </FieldPanelFullWidth>
      )}
      <ReferenceFlowSection
        epdVersionId={epdVersionId}
        unitDataSource={referenceFlowUnits}
        epdBasedOnUnit={selectedEPDBasedOnUnit?.value}
        errors={validationState.errors}
        setIsConversionFactorLock={setIsConversionFactorLock}
      />
      <ConversionFactorSection
        epdVersionId={epdVersionId}
        epdBasedOnUnit={lcaSpecification?.epdBasedOnUnit}
        isConversionFactorLock={isConversionFactorLock}
        errors={validationState.errors}
      />

      <FieldPanelFullWidth>
        <div>
          <WizardHTMLEditor
            name="additional.references"
            value={additionalInformation?.references}
            label="References"
            tooltip="A list of references shall be included, including references to the GPI (including version number) and relevant standards and PCRs (registration number, name and version)."
            tooltipPosition="right"
            onChanged={onChangeEpdAdditionalInformation}
            stripPastedStyles={true}
            required
            error={errors?.references}
          />
        </div>
      </FieldPanelFullWidth>

      {companyBelongsToDifferentGeographicalGroups && (
        <FieldPanelFullWidth>
          <ElementWithHelpBox>
            <CompilerSelectControlledComponent
              label="EPD Logo"
              name="customLogotype"
              options={logotypeOptions}
              value={selectedLogotype}
              onChanged={onChangeEPDDefinitionProperty}
              required={true}
              error={validationState.errors.customLogotype}
              placeholder="Select"
              isClearable={false}
            />
            <ExpandableHelpBox
              headerTemplate={(options) =>
                headerHelpBoxTemplate(options, 'How to select the appropriate logotype for your EPD?')
              }
              toggleable
            >
              <span>
                Choose the appropriate logotype to be displayed in the generated EPD:
                <p>
                  <li>
                    If a country-specific logotype is required, choose the{' '}
                    <HelpBoxBoldText>Licensee logotype.</HelpBoxBoldText>
                  </li>
                  <li>
                    For a region-specific logotype, select the <HelpBoxBoldText>CLC logotype</HelpBoxBoldText> option.
                  </li>
                </p>
                <HelpBoxBoldText>EPD verification is not required</HelpBoxBoldText> for this selection; it may be adjusted
                through an editorial update.
              </span>
            </ExpandableHelpBox>
          </ElementWithHelpBox>
        </FieldPanelFullWidth>
      )}
    </>
  );
};

const EllipsisLabelStyles: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export default DefinitionTab;
